import React, { useState, useRef, useEffect } from 'react';
import { useThemeLanguage } from '../context/ThemeLanguageContext';
import './ThemeLanguageBar.css';

const LANG_OPTIONS = [
  { code: 'he', label: 'עברית' },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'ar', label: 'العربية' },
  { code: 'am', label: 'አማርኛ' },
];

function GlobeIcon() {
  return (
    <svg className="theme-language-bar-globe" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg className="theme-language-bar-theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="theme-language-bar-theme-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.79A9 9 0 0 1 11.21 3 7 7 0 1 0 21 12.79z" />
    </svg>
  );
}

export default function ThemeLanguageBar() {
  const { theme, setTheme, language, setLanguage } = useThemeLanguage();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLabel = LANG_OPTIONS.find((o) => o.code === language)?.label ?? 'English';

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const isDay = theme === 'day';
  const themeLabel = isDay ? 'Switch to Dark Mode' : 'Switch to Day Mode';

  return (
    <div className="theme-language-bar">
      <div className="theme-language-bar-group">
        <button
          type="button"
          className="theme-language-bar-btn theme-language-bar-theme-toggle"
          onClick={() => setTheme(isDay ? 'dark' : 'day')}
          aria-pressed={isDay}
          aria-label={themeLabel}
        >
          {isDay ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>
      <div className="theme-language-bar-group theme-language-bar-dropdown-wrap" ref={dropdownRef}>
        <button
          type="button"
          className={`theme-language-bar-btn theme-language-bar-lang-trigger ${dropdownOpen ? 'open' : ''}`}
          onClick={() => setDropdownOpen((v) => !v)}
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
          aria-label="Language"
        >
          <GlobeIcon />
          <span className="theme-language-bar-lang-label">{currentLabel}</span>
          <span className="theme-language-bar-chevron" aria-hidden>▼</span>
        </button>
        {dropdownOpen && (
          <ul
            className="theme-language-bar-dropdown"
            role="listbox"
            aria-label="Language"
          >
            {LANG_OPTIONS.map((opt) => (
              <li key={opt.code} role="option" aria-selected={language === opt.code}>
                <button
                  type="button"
                  className={`theme-language-bar-dropdown-item ${language === opt.code ? 'active' : ''}`}
                  onClick={() => {
                    setLanguage(opt.code);
                    setDropdownOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
